import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { VoiceConnectionStatus } from '../../types/voice-demo';

type VisualizerState = 'idle' | 'recording' | 'speaking' | 'processing';

function mapStatus(status: VoiceConnectionStatus): VisualizerState {
  switch (status) {
    case 'disconnected':
      return 'idle';
    case 'connecting':
      return 'processing';
    case 'connected':
      return 'speaking';
    case 'recording':
      return 'recording';
  }
}

interface AudioVisualizerProps {
  status: VoiceConnectionStatus;
  mediaStream: MediaStream | null;
  color?: string;
  onClick?: () => void;
}

const BRAND_BLUE = 0x008af8;

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  status,
  mediaStream,
  color = '#008AF8',
  onClick,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const state = mapStatus(status);

  useEffect(() => {
    if (!mountRef.current) return;
    if (state === 'recording' && !mediaStream) return;

    if (state === 'processing') {
      mountRef.current.innerHTML = '';
      return;
    }

    const mountPoint = mountRef.current;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      mountPoint.clientWidth / mountPoint.clientHeight,
      0.1,
      1000,
    );
    camera.position.z = 3;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(mountPoint.clientWidth, mountPoint.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountPoint.innerHTML = '';
    mountPoint.appendChild(renderer.domElement);

    const particleCount = 2500;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const originalPositions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      const theta = i * 2.39996322973; // golden angle
      const y = 1 - (i / (particleCount - 1)) * 2;
      const radius = Math.sqrt(1 - y * y) * 1.5;
      positions[i3] = Math.cos(theta) * radius;
      positions[i3 + 1] = y * 1.5;
      positions[i3 + 2] = Math.sin(theta) * radius;
    }
    originalPositions.set(positions);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({ size: 0.025, sizeAttenuation: true });
    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let dataArray: Uint8Array<ArrayBuffer> | null = null;

    if (state === 'recording' && mediaStream) {
      audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(mediaStream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      dataArray = new Uint8Array(analyser.frequencyBinCount) as Uint8Array<ArrayBuffer>;
    }

    let animationFrameId: number;

    const animate = () => {
      const time = Date.now() * 0.0005;
      const positionAttribute = geometry.getAttribute('position') as THREE.BufferAttribute;

      if (state === 'recording') {
        material.color.set(color);
      } else if (state === 'speaking') {
        const pulse = Math.sin(time * 2) * 0.3 + 0.7;
        material.color.set(Math.floor(BRAND_BLUE * pulse));
      } else {
        material.color.set(BRAND_BLUE);
      }

      if (state === 'recording' && analyser && dataArray) {
        analyser.getByteFrequencyData(dataArray);
        const averageFrequency = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
        const distortion = (averageFrequency / 128) * 0.3;
        for (let i = 0; i < particleCount; i++) {
          const i3 = i * 3;
          const displacement = distortion * (Math.sin(i * 0.1 + time * 2) + 1);
          positionAttribute.setXYZ(
            i,
            originalPositions[i3] * (1 + displacement),
            originalPositions[i3 + 1] * (1 + displacement),
            originalPositions[i3 + 2] * (1 + displacement),
          );
        }
      } else {
        const speed = state === 'speaking' ? 0.5 : 1;
        for (let i = 0; i < particleCount; i++) {
          const i3 = i * 3;
          const displacement = 0.05 * Math.sin(i * 0.05 + time * speed);
          positionAttribute.setXYZ(
            i,
            originalPositions[i3] * (1 + displacement),
            originalPositions[i3 + 1] * (1 + displacement),
            originalPositions[i3 + 2] * (1 + displacement),
          );
        }
      }

      positionAttribute.needsUpdate = true;
      particles.rotation.y += 0.0005;
      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      void audioContext?.close();
      if (mountPoint) mountPoint.innerHTML = '';
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, [mediaStream, state, color]);

  return (
    <div
      className="relative flex items-center justify-center cursor-pointer transition-transform duration-500 hover:scale-105"
      style={{ width: 256, height: 256 }}
      onClick={state === 'idle' ? onClick : undefined}
    >
      <div ref={mountRef} className="w-full h-full" />

      {state === 'idle' && (
        <div className="absolute flex items-center justify-center w-24 h-24 rounded-full animate-pulse"
          style={{ backgroundColor: 'rgba(0, 138, 248, 0.1)' }}
        >
          <svg
            className="w-10 h-10"
            style={{ color: 'rgb(0, 138, 248)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

export default AudioVisualizer;
